<template>
    <div class="grid">
        <Toast/>
        <ConfirmDialog/>

        <!-- ========== Estado: módulo NO habilitado ========== -->
        <div class="col-12" style="max-width: 900px;" v-if="!loading && !status.enabled">
            <div class="card">
                <h4><i class="pi pi-book" style="font-size: 1.3rem; margin-right: 0.5rem;"></i>Módulo de Contabilidad</h4>
                <p style="max-width: 640px;">
                    Al habilitarlo, Contablus llevará <b>contabilidad formal de partida doble</b> en paralelo,
                    generada automáticamente desde tus ventas, compras y cobros — sin que tengas que ingresar
                    asientos manualmente. Obtendrás Estado de Resultados, Balance General y conciliación de
                    pasarelas de pago.
                </p>
                <Message severity="info" :closable="false">
                    Requiere que reportes tus transacciones en cuentas (caja, bancos, tarjetas).
                </Message>
                <Button label="Habilitar módulo" icon="pi pi-power-off" class="p-button-success mt-3"
                        :loading="working" @click="enableModule"/>
            </div>
        </div>

        <!-- ========== Estado: PENDIENTE — wizard de apertura ========== -->
        <div class="col-12" v-if="!loading && status.enabled && status.pending">
            <div class="card">
                <h4>Saldos de apertura</h4>
                <p style="max-width: 720px;">
                    Antes de arrancar, revisa el punto de partida de tu contabilidad. Los valores que podemos
                    calcular con la información de tu cuenta ya vienen llenos; el resto está en cero para que
                    <b>declares lo que solo tú sabes</b>: préstamos, muebles y equipos, utilidades acumuladas
                    de años anteriores. La línea <b>Capital</b> se calcula sola para que todo cuadre — revísala:
                    si se ve extraña, corrige los demás valores.
                </p>

                <div style="display: flex; gap: 0.5rem; align-items: center; flex-wrap: wrap;">
                    <Button :label="hideZeros ? 'Mostrar todas las cuentas' : 'Ocultar las que están en cero'"
                            :icon="hideZeros ? 'pi pi-eye' : 'pi pi-eye-slash'"
                            class="p-button-text p-button-sm" @click="hideZeros = !hideZeros"/>
                    <small v-if="hideZeros && zeroCount" style="color: #777;">
                        {{ zeroCount }} cuenta(s) en cero ocultas
                    </small>
                </div>

                <DataTable :value="visibleLines" :scrollable="true" scrollHeight="460px" class="mt-2"
                           rowGroupMode="subheader" groupRowsBy="accountType">
                    <template #groupheader="slotProps">
                        <span style="font-weight: 700;">{{ typeLabel(slotProps.data.accountType) }}</span>
                    </template>
                    <Column header="Concepto" style="min-width: 220px;">
                        <template #body="slotProps">
                            <span :style="slotProps.data.isPlug ? 'font-weight: 700;' : ''">
                                {{ slotProps.data.accountName }}
                                <i v-if="!slotProps.data.editable && !slotProps.data.isPlug" class="pi pi-lock ml-2" style="font-size: 0.8rem; color: #999;" v-tooltip="'Valor exacto del sistema'"></i>
                            </span>
                        </template>
                    </Column>
                    <Column header="Origen" style="min-width: 180px;">
                        <template #body="slotProps">
                            <small style="color: #777;">{{ slotProps.data.source }}</small>
                        </template>
                    </Column>
                    <Column header="Tipo" style="min-width: 110px;">
                        <template #body="slotProps">
                            <Tag :severity="slotProps.data.side === 'debit' ? 'success' : 'warning'"
                                 :value="slotProps.data.side === 'debit' ? 'Tengo' : 'Debo'"/>
                        </template>
                    </Column>
                    <Column header="Monto" style="min-width: 160px;">
                        <template #body="slotProps">
                            <InputNumber v-if="slotProps.data.editable"
                                         v-model="edits[slotProps.data.key]"
                                         mode="decimal" :minFractionDigits="2" :maxFractionDigits="2"
                                         style="width: 130px;" @input="recomputePlug"/>
                            <span v-else :style="slotProps.data.isPlug ? 'font-weight: 700;' : ''">
                                ${{ displayAmount(slotProps.data) }}
                            </span>
                        </template>
                    </Column>
                </DataTable>

                <div class="mt-3" style="display: flex; gap: 0.5rem; align-items: center; flex-wrap: wrap;">
                    <Button label="Recalcular" icon="pi pi-refresh" class="p-button-outlined"
                            :loading="working" @click="loadPreview"/>
                    <Button label="Confirmar y activar contabilidad" icon="pi pi-check"
                            class="p-button-success" :loading="working" @click="confirmOpening"/>
                    <small style="color: #777;">Al confirmar se fija la fecha de corte ({{ preview.cutoffDate }}) y el módulo empieza a registrar.</small>
                </div>
            </div>
        </div>

        <!-- ========== Estado: ACTIVO ========== -->
        <div class="col-12" style="max-width: 900px;" v-if="!loading && status.enabled && status.activated">
            <div class="card">
                <h4>Contabilidad activa
                    <Tag class="ml-2" :severity="status.mode === 'LIVE' ? 'success' : 'info'"
                         :value="status.mode === 'LIVE' ? 'PRODUCCIÓN' : 'MODO VALIDACIÓN'"/>
                </h4>
                <p>Fecha de corte: <b>{{ status.cutoffDate }}</b></p>

                <Message v-if="status.mode !== 'LIVE'" severity="info" :closable="false">
                    El módulo está en <b>modo validación</b>: los asientos se generan y puedes revisar los
                    reportes y el estado de salud. Cuando el Balance de Comprobación cuadre y la conciliación
                    esté limpia, pásalo a producción.
                </Message>

                <div class="mt-3" style="display: flex; gap: 0.5rem; flex-wrap: wrap;">
                    <Button v-if="status.mode !== 'LIVE'" label="Pasar a producción" icon="pi pi-check-circle"
                            class="p-button-success" :loading="working" @click="goLive"/>
                    <Button label="Ver salud del módulo" icon="pi pi-heart" class="p-button-outlined"
                            @click="$router.push('/dashboard/contabilidad/balance-comprobacion')"/>
                    <Button label="Reiniciar apertura" icon="pi pi-undo" class="p-button-outlined p-button-warning"
                            :loading="working" @click="confirmReset"/>
                    <Button label="Deshabilitar módulo" icon="pi pi-power-off" class="p-button-outlined p-button-danger"
                            :loading="working" @click="confirmDisable"/>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import httpRequest from '../../../service/httpRequest';
import EventBus from '@/AppEventBus';

export default {
    data() {
        return {
            loading: true,
            working: false,
            status: { enabled: false, pending: false, activated: false, hasOpening: false, mode: null, cutoffDate: null },
            preview: { cutoffDate: null, lines: [] },
            edits: {},   // { lineKey: monto } de las líneas editables
            hideZeros: false,  // la apertura ofrece TODAS las cuentas de balance
            editTick: 0,       // ver `visibleLines`
        }
    },
    computed: {
        // La apertura llega ordenada por código, que ya agrupa por tipo (1=Activo,
        // 2=Pasivo, 3=Patrimonio). Reordenamos igual porque el Capital (el plug)
        // viene al final y rompería el agrupado por subheader.
        sortedLines() {
            return [...this.preview.lines].sort(
                (a, b) => String(a.accountCode).localeCompare(String(b.accountCode))
            );
        },
        visibleLines() {
            const rows = this.hideZeros
                ? this.sortedLines.filter(line => this.hasValue(line))
                : this.sortedLines;
            // `editTick` entra como dependencia a propósito: la DataTable solo
            // repinta si cambia la identidad del array, y necesitamos que la fila
            // del Capital (el plug) se recalcule en vivo mientras se edita.
            void this.editTick;
            return [...rows];
        },
        zeroCount() {
            return this.sortedLines.filter(line => !this.hasValue(line)).length;
        },
    },
    async mounted() {
        await this.loadStatus();
        this.loading = false;
    },
    methods: {
        async loadStatus() {
            const response = await httpRequest.getAccountingStatus();
            if (response.status === 200) {
                this.status = response.data;
                if (this.status.enabled && this.status.pending) await this.loadPreview();
            } else {
                this.$toast.add({severity: 'error', summary: 'Error', detail: 'No se pudo consultar el estado del módulo', life: 4000});
            }
        },
        async loadPreview() {
            this.working = true;
            const response = await httpRequest.getAccountingOpeningPreview();
            this.working = false;
            if (response.status === 200) {
                this.preview = response.data;
                this.edits = {};
                for (const line of this.preview.lines) {
                    if (line.editable) this.edits[line.key] = parseFloat(line.amount);
                }
            } else {
                this.$toast.add({severity: 'error', summary: 'Error', detail: 'No se pudo calcular la apertura', life: 4000});
            }
        },
        typeLabel(type) {
            return {ASSET: 'Lo que tengo', LIABILITY: 'Lo que debo', EQUITY: 'Patrimonio'}[type] || type;
        },
        hasValue(line) {
            // El Capital (plug) se muestra siempre; el resto solo si tiene monto,
            // ya sea el calculado o uno que el usuario esté escribiendo.
            if (line.isPlug) return true;
            const edited = line.editable ? this.edits[line.key] : null;
            const amount = (edited !== null && edited !== undefined) ? edited : parseFloat(line.amount);
            return Math.abs(parseFloat(amount) || 0) > 0;
        },
        displayAmount(line) {
            if (line.isPlug) return this.computedPlug();
            return parseFloat(line.amount).toFixed(2);
        },
        computedPlug() {
            // Capital = descuadre con los valores editados (visible en vivo).
            let debit = 0, credit = 0;
            for (const line of this.preview.lines) {
                if (line.isPlug) continue;
                const amount = line.editable ? (this.edits[line.key] || 0) : parseFloat(line.amount);
                if (line.side === 'debit') debit += amount;
                else credit += amount;
            }
            return Math.abs(debit - credit).toFixed(2);
        },
        recomputePlug() {
            this.editTick += 1;  // fuerza el re-render de la fila Capital
        },
        async enableModule() {
            this.working = true;
            const response = await httpRequest.enableAccounting();
            this.working = false;
            if (response.status === 200) {
                this.$toast.add({severity: 'success', summary: 'Módulo habilitado', detail: 'Ahora revisa tus saldos de apertura', life: 4000});
                await this.refreshUserMenu();
                await this.loadStatus();
            } else {
                this.$toast.add({severity: 'error', summary: 'No se pudo habilitar', detail: response.error || 'Tu plan podría no incluir este módulo', life: 5000});
            }
        },
        async confirmOpening() {
            this.working = true;
            const overrides = {};
            for (const [key, value] of Object.entries(this.edits)) {
                overrides[key] = String(value || 0);
            }
            const response = await httpRequest.confirmAccountingOpening(overrides);
            this.working = false;
            if (response.status === 200) {
                this.$toast.add({severity: 'success', summary: 'Contabilidad activada', detail: 'Los asientos comenzarán a generarse automáticamente', life: 5000});
                this.status = response.data;
            } else {
                this.$toast.add({severity: 'error', summary: 'Error', detail: 'No se pudo confirmar la apertura', life: 4000});
            }
        },
        async goLive() {
            this.working = true;
            const response = await httpRequest.goLiveAccounting();
            this.working = false;
            if (response.status === 200) {
                this.$toast.add({severity: 'success', summary: 'En producción', detail: 'Los reportes son ahora oficiales', life: 4000});
                this.status = response.data;
            } else {
                this.$toast.add({severity: 'error', summary: 'Error', detail: response.error || 'Primero confirma los saldos de apertura', life: 4000});
            }
        },
        confirmReset() {
            this.$confirm.require({
                message: 'Se descartará el asiento de apertura y el módulo volverá a estado pendiente. ¿Continuar?',
                header: 'Reiniciar apertura',
                icon: 'pi pi-exclamation-triangle',
                acceptLabel: 'Sí, reiniciar',
                rejectLabel: 'Cancelar',
                accept: async () => {
                    this.working = true;
                    const response = await httpRequest.resetAccountingOpening();
                    this.working = false;
                    if (response.status === 200) {
                        this.status = response.data;
                        await this.loadPreview();
                    }
                },
            });
        },
        confirmDisable() {
            this.$confirm.require({
                message: 'El módulo dejará de generar asientos (los existentes se conservan). ¿Continuar?',
                header: 'Deshabilitar contabilidad',
                icon: 'pi pi-exclamation-triangle',
                acceptLabel: 'Sí, deshabilitar',
                rejectLabel: 'Cancelar',
                accept: async () => {
                    this.working = true;
                    const response = await httpRequest.disableAccounting();
                    this.working = false;
                    if (response.status === 200) {
                        await this.refreshUserMenu();
                        await this.loadStatus();
                    }
                },
            });
        },
        refreshUserMenu() {
            // Al habilitar o deshabilitar cambian las pantallas del libro en el menú
            // (`accountingActive`). App re-pide la sesión, la guarda y rearma el menú lateral:
            // guardar aquí el usuario en el store no bastaba, porque App no rearma el menú
            // cuando el store cambia y el menú seguía igual hasta recargar.
            EventBus.emit('entitlements_changed', {});
        },
    },
}
</script>
